import { FC, useCallback, useMemo } from 'react';
import { usePostHog } from '@posthog/react';
import TripManipulationForm from '@/components/functional/TripManipulationForm/TripManipulationForm.tsx';
import TripCreateStep from '@/_constants/TripCreateStep.ts';
import useTrips from '@/api/trips/useTrips.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { Alert } from 'react-daisyui';
import useTripCreate from '@/api/trips/useTripCreate.ts';
import useTripUpdate from '@/api/trips/useTripUpdate.ts';
import usePrepareDestination from '@/api/itineraries/usePrepareDestination.ts';
import shouldPrepareDestination from '@/utils/trip/shouldPrepareDestination.ts';
import type { OnCompleteArgs } from '@/components/functional/TripManipulationForm/TripManipulationForm.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import { useNavigate } from 'react-router-dom';
import { passToRoute } from '@/utils/passToRoute.ts';
import Routes from '@/router/Routes.ts';

const OnboardingTripCreatePage: FC = () => {
  const posthog = usePostHog();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const {
    data: trips,
    isLoading: isLoadingTrips,
    error: errorLoadingTrips,
  } = useTrips(!!user?.id, {
    user_id: user?.id,
  });

  const firstTrip = useMemo(() => {
    return trips?.[0] ?? null;
  }, [trips]);

  const { mutateAsync: createTrip } = useTripCreate();
  const { mutateAsync: updateTrip } = useTripUpdate();
  const { mutateAsync: prepareDestination } = usePrepareDestination();

  const prepareAhead = useCallback(
    (tripId: string) => {
      prepareDestination({ trip_id: tripId }).catch((e: unknown) => {
        console.error('Error preparing the destination: ', e);
        posthog?.captureException(e, { operation: 'prepare_destination', trip_id: tripId });
      });
    },
    [prepareDestination, posthog]
  );

  const handleTripCreateOrUpdate = useCallback(
    async ({ step, context }: OnCompleteArgs) => {
      try {
        if (firstTrip) {
          await updateTrip([{ data: context }, { id: firstTrip.id }]);
        }

        const tripId = firstTrip?.id ?? (await createTrip({ data: context, user_id: user!.id })).id;

        if (shouldPrepareDestination(step, context)) {
          prepareAhead(tripId);
        }
      } catch (e) {
        console.error('Error creating or updating trip in onboarding: ', e);
        posthog?.capture('onboarding_step_failed', {
          onboarding_step: 'trip_creation',
        });
        posthog?.captureException(e, {
          operation: firstTrip ? 'update_onboarding_trip' : 'create_onboarding_trip',
          onboarding_step: 'trip_creation',
        });
        throw new Error((e as Error)?.message ?? 'Error creating or updating trip');
      }
    },
    [firstTrip, createTrip, updateTrip, user, posthog, prepareAhead]
  );

  const handleTripComplete = useCallback(
    async ({ step }: OnCompleteArgs) => {
      if (step === TripCreateStep.MORE_INFO && !!firstTrip) {
        navigate(
          passToRoute(Routes.TRIP__CONFIRM, {
            id: firstTrip.id,
          })
        );
      }
    },
    [firstTrip, navigate]
  );

  return (
    <div className="flex flex-col gap-8 max-w-full">
      {errorLoadingTrips ? <Alert status="error">{errorLoadingTrips.message}</Alert> : null}
      {isLoadingTrips ? (
        <LoaderWrapper isLoading={isLoadingTrips} />
      ) : (
        <TripManipulationForm
          initialData={firstTrip?.data}
          onCompleteWhenDirty={handleTripCreateOrUpdate}
          onComplete={handleTripComplete}
        />
      )}
    </div>
  );
};

export default OnboardingTripCreatePage;
